import React, { useState } from "react";
import { Phone, ArrowDownLeft, ArrowUpRight, PhoneMissed, Radio, Bot, Calendar, ChevronDown } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

interface StatTile {
  icon: React.ReactNode;
  iconClassName: string;
  label: string;
  value: string;
  secondaryValue?: string;
  description: string;
}

const STATS: StatTile[] = [
  { icon: <Phone size={15} />, iconClassName: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300", label: "Total Calls", value: "546", secondaryValue: "205min", description: "All inbound and outbound calls during the selected period." },
  { icon: <ArrowDownLeft size={15} />, iconClassName: "bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400", label: "Inbound Calls", value: "12", secondaryValue: "25min", description: "Recent Inbound and Outbound call activity" },
  { icon: <ArrowUpRight size={15} />, iconClassName: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", label: "Outbound Calls", value: "12", secondaryValue: "25min", description: "Recent Inbound and Outbound call activity" },
  { icon: <PhoneMissed size={15} />, iconClassName: "bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400", label: "Missed Calls", value: "4", description: "AI Agents ready to receive calls" },
  { icon: <Radio size={15} />, iconClassName: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300", label: "Active Calls", value: "4", description: "AI Agents ready to receive calls" },
  { icon: <Bot size={15} />, iconClassName: "bg-gray-900 dark:bg-gray-700 text-white", label: "Agents", value: "4", description: "Numbers ready for call handling" },
];

// "Calls Overview" section — date range + channel filters (UI only, static
// for now, same convention as the CDP pages' as-yet-unwired filters) plus
// the 6 summary tiles.
export const OverviewStats: React.FC = () => {
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [channelOpen, setChannelOpen] = useState(false);
  const [channel, setChannel] = useState<"whatsapp" | "phone">("whatsapp");

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Calls Overview</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            See a snapshot of the contacts and customer records currently available from your uploaded data.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <button
              type="button"
              onClick={() => setDateRangeOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <Calendar size={14} />
              15 June - 22 June 2026
              <ChevronDown size={14} />
            </button>
            {dateRangeOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg z-10 p-2 text-xs text-gray-500 dark:text-gray-400">
                Date range picker coming soon.
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setChannelOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              {channel === "whatsapp" ? <FaWhatsapp className="text-green-500" size={14} /> : <Phone className="text-indigo-500" size={14} />}
              {channel === "whatsapp" ? "WhatsApp Call" : "Phone Call"}
              <ChevronDown size={14} />
            </button>
            {channelOpen && (
              <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg z-10 py-1">
                <button
                  type="button"
                  onClick={() => { setChannel("whatsapp"); setChannelOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <FaWhatsapp className="text-green-500" size={14} /> WhatsApp Call
                </button>
                <button
                  type="button"
                  onClick={() => { setChannel("phone"); setChannelOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Phone className="text-indigo-500" size={14} /> Phone Call
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {STATS.map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex flex-col gap-2 shadow-sm min-w-0">
            <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 min-w-0">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0 ${stat.iconClassName}`}>{stat.icon}</span>
              <span className="truncate">{stat.label}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{stat.value}</span>
              {stat.secondaryValue && <span className="text-xs sm:text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">{stat.secondaryValue}</span>}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{stat.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default OverviewStats;
